<template>
    <x-page title="Report Usia">
        <template #header-actions>
            <x-back-button
                :disabled="isLoading"
                :display-warning="false"
                :click-handler="handleBackButtonClick"
            />
        </template>

        <x-filter-box
            :onFilter="handleFilter"
            :onReset="resetFilter"
            :open="true"
        >
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <x-form-group :horizontal="false" label="Status Jemaat">
                        <x-input-member-statuses v-model="filters.statuses" />
                    </x-form-group>
                </div>
            </div>
        </x-filter-box>

        <div class="row">
            <div class="col-xs-12 col-md-6">
                <x-table :headers="['', 'Dari', 'Sampai', 'Label']" caption="Tabel Usia">
                    <tr v-for="breakpoint in breakpoints" :key="breakpoint.id">
                        <td>
                            <button
                                type="button"
                                class="btn btn-danger"
                                title="Hapus"
                                @click="deleteBreakpoint(breakpoint.id)"
                            >
                                <i class="fa fa-trash" />
                            </button>
                        </td>
                        <td>
                            <x-input-group>
                                <input
                                    type="number"
                                    class="form-control"
                                    v-model="breakpoint.start"
                                    placeholder="Dari"
                                    required
                                />
                            </x-input-group>
                        </td>
                        <td>
                            <x-input-group>
                                <input
                                    type="number"
                                    class="form-control"
                                    v-model="breakpoint.end"
                                    placeholder="Sampai"
                                    required
                                />
                            </x-input-group>
                        </td>
                        <td>
                            <input
                                type="text"
                                class="form-control"
                                v-model="breakpoint.label"
                                :placeholder="getLabelPlaceholder(breakpoint)"
                                required
                            />
                        </td>
                    </tr>
                    <template #footer="{ colspan }">
                        <tr>
                            <td :colspan="colspan">
                                <button
                                    class="btn btn-default"
                                    @click="addBreakpoint"
                                >
                                    <i class="fa fa-plus" /> Tambah
                                </button>
                                <button
                                    class="btn btn-primary"
                                    @click="saveBreakpoints"
                                    :disabled="isSaving"
                                >
                                    <i class="fa fa-save" /> Simpan & Refresh
                                    Chart
                                </button>
                            </td>
                        </tr>
                    </template>
                </x-table>
            </div>
            <div class="col-xs-12 col-md-6">
                <canvas :id="`pie-chart`"></canvas>
            </div>
        </div>
    </x-page>
</template>

<script>
import Chart from "chart.js/auto";
import { get } from "lodash";
import XAjaxDropdown from "../../../components/XAjaxDropdown.vue";
import XBackButton from "../../../components/XBackButton.vue";
import XFilterBox from "../../../components/XFilterBox.vue";
import XFormGroup from "../../../components/XFormGroup.vue";
import XInputMetrics from "../../../components/XInputMetrics.vue";
import XPage from "../../../components/XPage.vue";
import XPagination from "../../../components/XPagination.vue";
import XTable from "../../../components/XTable.vue";
import {
    emptyPromise,
    getConfig,
    handleGeneralXhrError,
    handleValidationXhrError,
} from "../../../helpers";
import FilterableMixin from "../../../mixins/FilterableMixin";
import PaginationMixin from "../../../mixins/PaginationMixin";
import PermissionMixin from "../../../mixins/PermissionMixin";
import XDumbPagination from "../../../components/XDumbPagination.vue";
import XPanel from "../../../components/XPanel.vue";
import XInputGroupStatisticTemplate from "../../../components/XInputGroupStatisticTemplate.vue";
import XHelpDefault from "../../../components/XHelpDefault.vue";
import XInputGroup from "../../../components/XInputGroup.vue";
import XInputMemberStatuses from "../../../components/XInputMemberStatuses.vue";

const defaultFilters = {
    statuses: [],
};
const settingKey = "member_age_breakpoints";
let pieChart = null;

export default {
    beforeRouteUpdate(to, from, next) {
        this.fetchResources(to);
        next();
    },
    components: {
        XTable,
        XPage,
        XFormGroup,
        XFilterBox,
        XAjaxDropdown,
        XPagination,
        XBackButton,
        XInputMetrics,
        XDumbPagination,
        XPanel,
        XInputGroupStatisticTemplate,
        XHelpDefault,
        XInputGroup,
        XInputMemberStatuses,
    },
    created() {},
    data() {
        return {
            breakpoints: getBreakpoints(),
            filters: { ...defaultFilters },
            isLoading: false,
            isSaving: false,
            resources: [],
        };
    },
    methods: {
        addBreakpoint() {
            this.breakpoints.push({
                id: new Date().getTime(),
                label: null,
                start: null,
                end: null,
            });
        },
        deleteBreakpoint(id) {
            this.breakpoints = this.breakpoints.filter(
                (breakpoint) => breakpoint.id !== id
            );
        },
        fetchResources() {
            this.isLoading = true;

            if (this.breakpoints.length < 1) {
                return emptyPromise().finally(() => {
                    this.isLoading = false;
                });
            }

            const request = axios
                .get(`/api/reports/member-age`, {
                    params: {
                        breakpoints: this.breakpoints,
                        ...this.filters,
                    },
                })
                .then((response) => {
                    this.resources = response.data.data;
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        getLabelPlaceholder(breakpoint) {
            return `${get(breakpoint, "start", 0)} - ${get(
                breakpoint,
                "end",
                0
            )}`;
        },
        handleBackButtonClick(router) {
            router.replace("/admin/reports");
        },
        handleFilter() {
            return this.fetchResources().then(() => {
                this.updatePieChart();
            });
        },
        initPieChart() {
            const ctx = document.getElementById(`pie-chart`);
            pieChart = new Chart(ctx, {
                type: "doughnut",
                data: {
                    labels: this.resources.map((resource) => resource.label),
                    datasets: [],
                },
            });
            updatePieChart(pieChart, this.resources);
        },
        resetFilter() {
            this.filters = { ...defaultFilters };
            this.handleFilter();
        },
        saveBreakpoints() {
            this.isSaving = true;
            this.isLoading = true;
            const request = axios
                .put(`/api/settings/${settingKey}`, {
                    value: this.breakpoints,
                })
                .then(() => {
                    this.$notify({
                        type: "success",
                        title: `Berhasil`,
                        text: `Setting disimpan!`,
                    });
                    this.fetchResources().then(() => {
                        this.updatePieChart();
                    });
                });
            return handleValidationXhrError(request).finally(() => {
                this.isSaving = false;
                this.isLoading = false;
            });
        },
        updatePieChart() {
            updatePieChart(pieChart, this.resources);
        },
    },
    mixins: [FilterableMixin, PaginationMixin, PermissionMixin],
    mounted() {
        this.fetchResources().then(() => {
            this.initPieChart();
        });
    },
};

function getBreakpoints() {
    const breakpoints = getConfig(settingKey);
    if (!breakpoints) {
        return [];
    }
    return JSON.parse(breakpoints);
}

function updatePieChart(chart, resources) {
    let colors = resources.map((resource, index) => {
        const oldColor = get(chart, `data.datasets.backgroundColor.${index}`);
        return (
            oldColor || `#${Math.floor(Math.random() * 16777215).toString(16)}`
        );
    });

    chart.data.labels = resources.map((resource) => resource.label);
    chart.data.datasets = [
        {
            data: resources.map((resource) =>
                parseInt(get(resource, "value", 0))
            ),
            backgroundColor: colors,
            hoverOffset: 4,
        },
    ];
    chart.update();
}
</script>